import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const NAVY = "#0e1e39";
const GOLD = "#c8a24c";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password, remember);
      navigate("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "14px 15px",
    fontSize: 15,
    fontFamily: "inherit",
    border: "1.5px solid #e1e5ec",
    borderRadius: 11,
    outline: "none",
    color: NAVY,
    marginBottom: 18,
    transition: "border-color 150ms ease, box-shadow 150ms ease",
  };
  const labelStyle = {
    display: "block",
    fontSize: 12.5,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 7,
  };
  const focusOn = (e) => {
    e.target.style.borderColor = NAVY;
    e.target.style.boxShadow = "0 0 0 3px rgba(14,30,57,0.08)";
  };
  const focusOff = (e) => {
    e.target.style.borderColor = "#e1e5ec";
    e.target.style.boxShadow = "none";
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-5 sm:p-10 relative overflow-hidden"
      style={{
        fontFamily: "Manrope, system-ui, sans-serif",
        background: "radial-gradient(900px 700px at 78% 50%, #15294a, #0a1526 70%)",
      }}
      data-testid="login-page"
    >
      {/* dotted texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.05,
          backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
          backgroundSize: "26px 26px",
        }}
      />
      {/* gold glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "-8%", right: "-6%", width: 520, height: 520, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(200,162,76,0.16), transparent 68%)",
          animation: "drift 16s ease-in-out infinite",
        }}
      />

      <div
        className="relative z-[2] w-[880px] max-w-full flex bg-white overflow-hidden"
        style={{ borderRadius: 22, boxShadow: "0 34px 80px -26px rgba(0,0,0,0.6)" }}
      >
        {/* LEFT — brand panel */}
        <div
          className="hidden md:flex w-[344px] shrink-0 flex-col text-white"
          style={{ padding: "46px 38px", background: "linear-gradient(160deg, #12233f, #0a1526)" }}
        >
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontFamily: "Spectral, serif", fontWeight: 600, fontSize: 27 }}>Incheon CX</div>
            <div style={{ fontSize: 11.5, letterSpacing: 3.5, color: GOLD, fontWeight: 700, marginTop: 7 }}>
              COMMAND CENTER
            </div>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <div
              style={{
                textTransform: "uppercase", letterSpacing: 4,
                fontSize: 10.5, fontWeight: 700, color: GOLD,
              }}
            >
              The Incheon Promise
            </div>
            <div style={{ fontFamily: "Spectral, serif", fontSize: 35, fontWeight: 400, lineHeight: 1.14, marginTop: 12 }}>
              Experience
              <br />
              <span style={{ fontStyle: "italic", color: "#e6d9b6" }}>Excellence.</span>
            </div>
            <p style={{ color: "#9fb0c6", fontSize: 13.5, lineHeight: 1.6, marginTop: 18 }}>
              Complaint intelligence for the Incheon Kia dealership group — RAG aging, escalations and outlet
              leaderboards in one place.
            </p>
          </div>
        </div>

        {/* RIGHT — form panel */}
        <div className="flex-1 flex flex-col justify-center" style={{ padding: "44px 32px 40px" }}>
          <div className="sm:px-3">
            <img
              src="/incheon-kia-logo.png"
              alt="Incheon Kia — Experience Excellence"
              style={{ height: 46, width: "auto", objectFit: "contain", marginBottom: 30, alignSelf: "flex-start" }}
            />
            <div style={{ fontFamily: "Spectral, serif", fontSize: 28, fontWeight: 600, color: NAVY }}>Sign in</div>
            <div style={{ color: "#6b7280", fontSize: 14, marginTop: 4, marginBottom: 28 }}>
              Sign in to review CX performance.
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4" data-testid="login-error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={submit}>
              <label htmlFor="email" style={labelStyle}>Email</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@incheonkia.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
                style={inputStyle}
                data-testid="login-email-input"
              />
              <label htmlFor="password" style={labelStyle}>Password</label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
                style={inputStyle}
                data-testid="login-password-input"
              />

              <div className="flex items-center justify-between" style={{ marginBottom: 26 }}>
                <label
                  className="flex items-center cursor-pointer"
                  style={{ gap: 8, fontSize: 13.5, color: "#374151" }}
                >
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: NAVY, cursor: "pointer" }}
                    data-testid="login-remember-checkbox"
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  onClick={() =>
                    toast.info("Please contact AGM Customer Care to reset your password.", { duration: 5000 })
                  }
                  className="hover:text-[#c8a24c] transition-colors"
                  style={{ fontSize: 13.5, fontWeight: 600, color: NAVY, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="hover:!bg-[#15294a] transition-colors disabled:opacity-70"
                style={{
                  width: "100%", padding: 15, fontSize: 15, fontWeight: 700,
                  fontFamily: "inherit", color: "#fff", background: NAVY,
                  border: "none", borderRadius: 11, cursor: "pointer", letterSpacing: 0.3,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
                data-testid="login-submit-button"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                Sign in
              </button>
            </form>

            <div style={{ marginTop: 22, fontSize: 12, color: "#8a94a4", textAlign: "center" }}>
              Access is provisioned by Incheon Connect.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
